import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export function PrivacyPage() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — RegattaReplay™</title>
        <meta
          name="description"
          content="Privacy Policy for RegattaReplay™ — how we collect, use, and protect your data."
        />
      </Helmet>
      <div className="max-w-[720px] mx-auto px-5 py-12">
        <p className="text-sm text-muted-foreground mb-1">Last Updated: 2026-06-25</p>
        <h1 className="text-3xl font-bold text-navy-900 mb-8">Privacy Policy</h1>

        {/* Plain-Language Summary */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-10">
          <p className="text-sm font-medium text-navy-900 mb-3">
            Plain-Language Summary
          </p>
          <p className="text-sm text-muted-foreground mb-2 italic">
            This summary helps you understand the key points. The full policy below is
            legally binding.
          </p>
          <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
            <li>
              <strong>We collect minimal data.</strong> Your email, name, GPS tracks you
              upload, and your activity on the platform.
            </li>
            <li>
              <strong>We also collect publicly available race results.</strong> If your
              name appears in a race result on a club website, we may display it here.
              You can request removal.
            </li>
            <li>
              <strong>We use your data to run the platform.</strong> Race replays,
              performance analytics, crew matching — that's it.
            </li>
            <li>
              <strong>We create anonymous aggregate products.</strong> Fleet analytics,
              race density maps. These don't identify you.
            </li>
            <li>
              <strong>We don't sell your personal data.</strong> We may have sponsors,
              but we don't sell individual user data.
            </li>
            <li>
              <strong>You can delete your data.</strong> Email us and we'll process your
              request within 30 days.
            </li>
            <li>
              <strong>GPS tracks are sensitive.</strong> We treat location data with care
              and give you control over visibility.
            </li>
          </ul>
        </div>

        {/* Section 1 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">1. Who We Are</h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            RegattaReplay™ is operated by Tario Marine Technologies, a Canadian company based
            in Ontario. For privacy inquiries, contact:
          </p>
          <ul className="text-sm text-slate-700 list-none space-y-1">
            <li>
              <strong>Privacy Contact</strong>:{' '}
              <a
                href="mailto:privacy@regattazone.com"
                className="text-brand-ink hover:underline"
              >
                privacy@regattazone.com
              </a>
            </li>
            <li>
              <strong>Mailing Address</strong>: Toronto, Ontario, Canada
            </li>
          </ul>
        </section>

        {/* Section 2 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            2. What Information We Collect
          </h2>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            2.1 Information You Provide
          </h3>
          <div className="overflow-x-auto mb-4">
            <table className="text-sm text-slate-700 w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-4 font-semibold">Data</th>
                  <th className="text-left py-2 pr-4 font-semibold">When</th>
                  <th className="text-left py-2 font-semibold">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 pr-4 font-medium">Email address</td>
                  <td className="py-2 pr-4">Account creation (magic link login)</td>
                  <td className="py-2">Authentication, account communications</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Display name</td>
                  <td className="py-2 pr-4">Account setup</td>
                  <td className="py-2">Profile identification</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">GPS track files</td>
                  <td className="py-2 pr-4">Track upload (VKX, GPX, FIT, CSV)</td>
                  <td className="py-2">Race replays, performance analytics</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Photos and videos</td>
                  <td className="py-2 pr-4">Content upload (future feature)</td>
                  <td className="py-2">Race documentation, community sharing</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Club affiliation</td>
                  <td className="py-2 pr-4">Profile setup</td>
                  <td className="py-2">Crew matching, community features</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Sailing certifications</td>
                  <td className="py-2 pr-4">Profile setup (Find section)</td>
                  <td className="py-2">Crew matching</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Boat information</td>
                  <td className="py-2 pr-4">Profile/registration</td>
                  <td className="py-2">Race association, fleet features</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            2.2 Information We Collect Automatically
          </h3>
          <div className="overflow-x-auto mb-4">
            <table className="text-sm text-slate-700 w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-4 font-semibold">Data</th>
                  <th className="text-left py-2 pr-4 font-semibold">How</th>
                  <th className="text-left py-2 font-semibold">Purpose</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 pr-4 font-medium">Usage data</td>
                  <td className="py-2 pr-4">Platform interaction</td>
                  <td className="py-2">Service improvement, analytics</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Device information</td>
                  <td className="py-2 pr-4">Browser/app metadata</td>
                  <td className="py-2">Technical support, compatibility</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">IP address</td>
                  <td className="py-2 pr-4">Server logs</td>
                  <td className="py-2">Security, abuse prevention</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            2.3 Information We Collect from Public Sources
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            We collect publicly available race results from sailing club websites, race
            management platforms (YachtScoring, ClubSpot, Sailwave), and sailing
            federation publications. This includes:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1 mb-3">
            <li>Event names, dates, venues, and organizing clubs</li>
            <li>Boat names, sail numbers, and class designations</li>
            <li>
              <strong>Skipper and crew names</strong> as published in official race
              results
            </li>
            <li>Finishing positions, elapsed times, and race scores</li>
            <li>Club affiliations and home ports</li>
          </ul>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            <strong>Why we collect this</strong>: Race results are public records of
            sporting events. We aggregate them to create a comprehensive sailing records
            archive that serves the sailing community — the same purpose for which they
            were originally published.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Our crawler identifies itself</strong>: We use the User-Agent{' '}
            <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">
              RegattaReplayCrawler/1.0 (+https://regattazone.com)
            </code>{' '}
            and respect <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">robots.txt</code>{' '}
            directives.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            2.4 Information We Do NOT Collect
          </h3>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>Home addresses or phone numbers</li>
            <li>Financial or payment information (the service is free)</li>
            <li>Health or biometric data</li>
            <li>Social insurance / social security numbers</li>
            <li>Data from authenticated/restricted pages</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            3. How We Use Your Information
          </h2>
          <div className="overflow-x-auto">
            <table className="text-sm text-slate-700 w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-4 font-semibold">Purpose</th>
                  <th className="text-left py-2 pr-4 font-semibold">Data Used</th>
                  <th className="text-left py-2 font-semibold">Legal Basis</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 pr-4 font-medium">Account management</td>
                  <td className="py-2 pr-4">Email, name</td>
                  <td className="py-2">Consent (account creation)</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Race replays</td>
                  <td className="py-2 pr-4">GPS tracks, boat info</td>
                  <td className="py-2">Consent (you upload tracks)</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Performance analytics</td>
                  <td className="py-2 pr-4">GPS tracks, race results</td>
                  <td className="py-2">Consent + legitimate interest</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Crew matching</td>
                  <td className="py-2 pr-4">Profile, certifications, preferences</td>
                  <td className="py-2">Consent (you opt into Find)</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Public race archive</td>
                  <td className="py-2 pr-4">Publicly scraped results</td>
                  <td className="py-2">Legitimate interest</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Aggregate analytics</td>
                  <td className="py-2 pr-4">Anonymized community data</td>
                  <td className="py-2">Legitimate interest</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Service communications</td>
                  <td className="py-2 pr-4">Email</td>
                  <td className="py-2">Consent + legitimate interest</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Security</td>
                  <td className="py-2 pr-4">IP, usage data</td>
                  <td className="py-2">Legitimate interest</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            4. How We Share Your Information
          </h2>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.1 With Other Users
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            Your race replays, race history, and profile information are visible to other
            users of the Service based on your privacy settings. Multi-participant race
            replays are visible to all participants and, if published, to the public.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.2 With Race Organizers and Clubs
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            If you participate in an event hosted on RegattaReplay™, the organizing club
            may see aggregate participation data for their events. We do not share your
            personal contact information with clubs without your consent.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.3 Aggregate and Anonymized Data
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            We may share anonymized, aggregated data with partners, sponsors, sailing
            organizations, and the public. This data does not identify individual
            sailors. Examples include fleet density maps, race participation trends, and
            wind pattern analysis.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.4 Legal Requirements
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            We may disclose your information when required by law, regulation, legal
            process, or governmental request.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            4.5 What We Do NOT Do
          </h3>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>
              We <strong>do not sell</strong> your personal data
            </li>
            <li>
              We <strong>do not share</strong> your email address with third parties for
              marketing
            </li>
            <li>
              We <strong>do not use</strong> your GPS tracks to contact you for purposes
              unrelated to the Service
            </li>
            <li>
              We <strong>do not provide</strong> individual tracking data to third
              parties
            </li>
          </ul>
        </section>

        {/* Section 5 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            5. GPS and Location Data
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            GPS track data is sensitive information that reveals where you were at
            specific times. We take special care with this data:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>
              <strong>You choose what to upload.</strong> We only receive GPS tracks you
              voluntarily submit.
            </li>
            <li>
              <strong>Visibility controls.</strong> You can control whether your tracks
              are publicly visible, visible only to race participants, or private.
            </li>
            <li>
              <strong>Accuracy disclaimer.</strong> GPS tracks are approximate and
              subject to device and environmental errors.
            </li>
            <li>
              <strong>No real-time tracking.</strong> We process historical track files —
              we do not track your location in real time.
            </li>
            <li>
              <strong>Anonymization in aggregates.</strong> When we create aggregate
              products (fleet analytics, heatmaps), individual tracks are anonymized so
              they cannot be traced back to you.
            </li>
          </ul>
        </section>

        {/* Section 6 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            6. Data Retention
          </h2>
          <div className="overflow-x-auto">
            <table className="text-sm text-slate-700 w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 pr-4 font-semibold">Data Type</th>
                  <th className="text-left py-2 font-semibold">Retention Period</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr>
                  <td className="py-2 pr-4 font-medium">
                    Account data (email, name, profile)
                  </td>
                  <td className="py-2">Until you delete your account</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Raw GPS track files</td>
                  <td className="py-2">
                    Until you delete them or close your account
                  </td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">
                    Race replays (multi-participant)
                  </td>
                  <td className="py-2">
                    Retained with your identifiers anonymized after account deletion
                  </td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">
                    Publicly collected race results
                  </td>
                  <td className="py-2">
                    Retained until removal requested; historical results are part of the
                    public sporting record
                  </td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">
                    Aggregate/anonymized data
                  </td>
                  <td className="py-2">
                    Retained indefinitely (does not identify individuals)
                  </td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-medium">Server logs</td>
                  <td className="py-2">90 days</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 7 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">7. Your Rights</h2>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            7.1 Access and Portability
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            You have the right to access your personal data and request a copy in a
            portable format. Contact{' '}
            <a
              href="mailto:privacy@regattazone.com"
              className="text-brand-ink hover:underline"
            >
              privacy@regattazone.com
            </a>
            .
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            7.2 Correction
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            You can update your profile information at any time through the Service. For
            corrections to race results, contact us.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            7.3 Deletion (Right to Erasure)
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            You may request deletion of your personal data at any time:
          </p>
          <ul className="text-sm text-slate-700 list-none space-y-1 mb-3">
            <li>
              <strong>Email</strong>:{' '}
              <a
                href="mailto:privacy@regattazone.com"
                className="text-brand-ink hover:underline"
              >
                privacy@regattazone.com
              </a>
            </li>
            <li>
              <strong>Processing time</strong>: Within 30 days
            </li>
          </ul>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            Upon a deletion request, we will:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
            <li>Delete your account and profile information</li>
            <li>Delete your raw GPS track files from our storage</li>
            <li>
              Anonymize your identifiers in any multi-participant replays (the track
              geometry is retained but your name/identifiers are removed)
            </li>
            <li>Retain anonymized aggregate data (does not identify you)</li>
          </ul>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            7.4 Objection
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            You may object to our processing of your personal data collected from public
            sources (race results). We will honor reasonable objection requests.
          </p>

          <h3 className="text-base font-semibold text-navy-900 mb-2 mt-5">
            7.5 Restriction
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed">
            You may request that we restrict processing of your personal data in certain
            circumstances.
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            8. Children's Privacy
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            The Service is not directed to children under 13. We do not knowingly collect
            personal information from children under 13. If we learn that we have
            collected personal information from a child under 13, we will delete it.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            Children between 13 and 18 may use the Service with parental or guardian
            consent. Parents or guardians may contact us to request access to, correction
            of, or deletion of their child's personal data.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Regarding publicly collected race results</strong>: Junior sailors'
            names may appear in publicly posted race results. Parents or guardians may
            request removal of a minor's name from our platform by contacting{' '}
            <a
              href="mailto:privacy@regattazone.com"
              className="text-brand-ink hover:underline"
            >
              privacy@regattazone.com
            </a>
            .
          </p>
        </section>

        {/* Section 9 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            9. International Data Transfers
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            RegattaReplay™ is based in Canada. Your data may be processed in Canada and
            the United States (where our cloud infrastructure is hosted). Canada has been
            recognized by the European Commission as providing an adequate level of data
            protection under GDPR.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            If you are located in the European Economic Area (EEA), United Kingdom, or
            other jurisdictions with data protection laws, your data will be transferred
            to and processed in Canada and the US. We rely on Canada's adequacy
            determination and standard contractual clauses where applicable.
          </p>
        </section>

        {/* Section 10 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">10. Security</h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            We implement reasonable technical and organizational measures to protect your
            personal data, including:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1 mb-3">
            <li>Encrypted data transmission (HTTPS/TLS)</li>
            <li>Encrypted data storage at rest (AWS S3, DynamoDB)</li>
            <li>Access controls and authentication</li>
            <li>Regular security reviews</li>
          </ul>
          <p className="text-sm text-slate-700 leading-relaxed">
            No system is 100% secure. We cannot guarantee absolute security of your data.
          </p>
        </section>

        {/* Section 11 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            11. Cookies and Tracking
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            We use essential cookies for authentication and session management. We do not
            use third-party advertising trackers. We may use privacy-respecting analytics
            to understand how the Service is used.
          </p>
        </section>

        {/* Section 12 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            12. Canadian Privacy Law (PIPEDA)
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            This Privacy Policy complies with Canada's Personal Information Protection
            and Electronic Documents Act (PIPEDA). Under PIPEDA, you have the right to:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1 mb-3">
            <li>Know what personal information we hold about you</li>
            <li>
              Challenge the accuracy and completeness of your information
            </li>
            <li>Have your information amended or deleted</li>
            <li>
              Withdraw consent (subject to legal or contractual restrictions)
            </li>
          </ul>
          <p className="text-sm text-slate-700 leading-relaxed">
            If you have concerns about our privacy practices, you may file a complaint
            with the Office of the Privacy Commissioner of Canada at{' '}
            <a
              href="https://www.priv.gc.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-ink hover:underline"
            >
              www.priv.gc.ca
            </a>
            .
          </p>
        </section>

        {/* Section 13 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            13. European Privacy Law (GDPR)
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            If you are located in the European Economic Area, you have additional rights
            under the General Data Protection Regulation (GDPR), including the rights
            described in Section 7.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            <strong>Legal bases for processing</strong>:
          </p>
          <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1 mb-3">
            <li>
              <strong>Consent</strong>: Account creation, GPS track uploads, crew
              matching
            </li>
            <li>
              <strong>Legitimate Interest</strong>: Public race result aggregation,
              service improvement, security
            </li>
            <li>
              <strong>Contract Performance</strong>: Providing the Service features you
              use
            </li>
          </ul>
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            <strong>EU Representative</strong>: To be appointed — required under GDPR
            Art. 27 before regular EU data processing.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Data Protection Authority</strong>: You have the right to lodge a
            complaint with your local data protection authority.
          </p>
        </section>

        {/* Section 14 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            14. Changes to This Policy
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed">
            We may update this Privacy Policy from time to time. We will notify you of
            material changes by email at least 30 days before they take effect. The "Last
            Updated" date at the top indicates when the policy was last revised.
          </p>
        </section>

        {/* Section 15 */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-navy-900 mb-3">
            15. Contact Us
          </h2>
          <p className="text-sm text-slate-700 leading-relaxed mb-2">
            For any privacy-related questions, requests, or concerns:
          </p>
          <ul className="text-sm text-slate-700 list-none space-y-1">
            <li>
              <strong>Email</strong>:{' '}
              <a
                href="mailto:privacy@regattazone.com"
                className="text-brand-ink hover:underline"
              >
                privacy@regattazone.com
              </a>
            </li>
            <li>
              <strong>Mailing Address</strong>: Toronto, Ontario, Canada
            </li>
            <li>
              <strong>Response Time</strong>: We aim to respond within 30 days
            </li>
          </ul>
        </section>
      </div>
    </>
  );
}
